import { useState } from "react";
import { useReferringDoctors, type ReferringDoctor } from "@/lib/queries";
import { useCreateReferringDoctor, useUpdateReferringDoctor, useDeleteReferringDoctor } from "@/lib/mutations";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Search, Pencil, Trash, Printer } from "lucide-react";
import { GENDERS } from "@emr/shared/constants";
import { validatePhone } from "@emr/shared/india";
import { printDoctorLabel } from "@/lib/printLabel";

function DoctorFormDialog({ open, onOpenChange, doctor }: { open: boolean; onOpenChange: (v: boolean) => void; doctor?: ReferringDoctor | null }) {
  const [form, setForm] = useState({
    name: doctor?.name ?? "",
    gender: doctor?.gender ?? "Male",
    designation: doctor?.designation ?? "",
    clinicHospital: doctor?.clinicHospital ?? "",
    address: doctor?.address ?? "",
    clinicPhone: doctor?.clinicPhone ?? "",
    personalPhone: doctor?.personalPhone ?? "",
    email: doctor?.email ?? "",
  });
  const [err, setErr] = useState<string | null>(null);
  const create = useCreateReferringDoctor();
  const update = useUpdateReferringDoctor();

  const submit = async () => {
    setErr(null);
    if (!form.name || !form.designation || !form.clinicHospital || !form.address) {
      setErr("Name, designation, clinic/hospital, and address are required.");
      return;
    }
    if ((form.clinicPhone && !validatePhone(form.clinicPhone)) || (form.personalPhone && !validatePhone(form.personalPhone))) {
      setErr("Invalid phone (10-digit mobile or 0-STD landline).");
      return;
    }
    try {
      const body = {
        ...form,
        clinicPhone: form.clinicPhone || null,
        personalPhone: form.personalPhone || null,
        email: form.email || null,
        // Optimistic lock token (2026-10-31 guard batch) — the loaded row's
        // updatedAt; engages PATCH /referring-doctors/:id's existing 409
        // stale-write guard. Included on create too (harmless — the POST
        // route ignores it).
        updatedAt: doctor?.updatedAt,
      };
      if (doctor) await update.mutateAsync({ id: doctor.id, body });
      else await create.mutateAsync(body);
      onOpenChange(false);
    } catch { /* handled */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{doctor ? "Edit referring doctor" : "Add referring doctor"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div>
            <Label>Gender</Label>
            <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v as "Male" | "Female" | "Other" })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Designation *</Label>
            <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="e.g. Consultant Neurologist" />
          </div>
          <div><Label>Clinic / Hospital *</Label><Input value={form.clinicHospital} onChange={(e) => setForm({ ...form, clinicHospital: e.target.value })} /></div>
          <div><Label>Address *</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div><Label>Clinic phone</Label><Input value={form.clinicPhone} onChange={(e) => setForm({ ...form, clinicPhone: e.target.value })} placeholder="10-digit mobile or 0-STD landline" /></div>
          <div><Label>Personal phone</Label><Input value={form.personalPhone} onChange={(e) => setForm({ ...form, personalPhone: e.target.value })} placeholder="10-digit mobile or 0-STD landline" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          {err && <p className="text-sm text-red-500">{err}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={create.isPending || update.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ReferringDoctorsPage() {
  const { isAdmin, permissions } = useAuth();
  const canCreate = isAdmin || permissions.has("referringdoctors.create");
  const canEdit = isAdmin || permissions.has("referringdoctors.edit");
  const canDelete = isAdmin || permissions.has("referringdoctors.delete");
  const [q, setQ] = useState("");
  const [editDoctor, setEditDoctor] = useState<ReferringDoctor | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  // Session counter bumps on every open so the dialog remounts fresh each time
  // and useState re-initializes from the current doctor prop (see SCHEMA.md §18 2026-07-20).
  const [sessionId, setSessionId] = useState(0);
  const openAdd = () => { setEditDoctor(null); setSessionId((n) => n + 1); setAddOpen(true); };
  const openEdit = (d: ReferringDoctor) => { setEditDoctor(d); setSessionId((n) => n + 1); setAddOpen(true); };
  const { data, isLoading } = useReferringDoctors(q.length >= 2 ? q : "");
  const deleteMut = useDeleteReferringDoctor();
  const rows = Array.isArray(data) ? data : (data as any)?.rows ?? [];

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Referring Doctors</h1>
        {canCreate && <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={openAdd}><Plus size={16} className="mr-1" /> Add Doctor</Button>}
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <Input placeholder="Search by name / designation / clinic / phone (2+ chars)…" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500">
                <TableHead>Name</TableHead><TableHead>Designation</TableHead><TableHead>Clinic/Hospital</TableHead><TableHead>Address</TableHead><TableHead>Clinic phone</TableHead><TableHead>Personal phone</TableHead><TableHead>Email</TableHead><TableHead>Gender</TableHead><TableHead className="w-28">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? <TableRow><TableCell colSpan={9}><Skeleton className="h-10" /></TableCell></TableRow> :
               rows.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center text-sm text-muted py-8">No referring doctors</TableCell></TableRow> :
               rows.map((d: ReferringDoctor) => (
                <TableRow key={d.id} className="even:bg-cream-50/50">
                  <TableCell className="font-medium">{d.name}</TableCell>
                  <TableCell>{d.designation}</TableCell>
                  <TableCell>{d.clinicHospital}</TableCell>
                  <TableCell className="text-xs text-muted">{d.address}</TableCell>
                  <TableCell>{d.clinicPhone ?? "—"}</TableCell>
                  <TableCell>{d.personalPhone ?? "—"}</TableCell>
                  <TableCell className="text-xs">{d.email ?? "—"}</TableCell>
                  <TableCell>{d.gender}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" title="Print label" onClick={() => printDoctorLabel(d)}><Printer size={14} /></Button>
                      {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(d)}><Pencil size={14} /></Button>}
                      {canDelete && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => deleteMut.mutate(d.id)}><Trash size={14} /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <DoctorFormDialog key={sessionId} open={addOpen} onOpenChange={setAddOpen} doctor={editDoctor} />
    </div>
  );
}
